import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronRight, CircleCheck, CircleStop, LoaderCircle, RotateCcw, Table2 } from 'lucide-react';
import { getTourActionLabel, isCampaignLevelActionNotification } from '../utils/actionNotifications';
import { getNeedsUserInputLabel, getActionCardDetails, getActionCardPropertyLabel } from '../utils/actionCardDetails';
import { describeBlockedOutbox } from '../utils/mailboxConnection';
import { ConnectMailboxButton } from './MailboxConnectionBanner';

import { campaignIsMarkedComplete } from '../utils/campaignCompletion';

export { campaignIsMarkedComplete } from '../utils/campaignCompletion';

export default function ClientRow({
  client,
  onOpen,
  onOpenSheet,
  onStart,
  onActionClick,
  onToggleConversations,
  hasActionPending = false,
  actionableNotification = null,
  allActionNotifications = [],
  isConversationsExpanded = false,
  notificationCount = 0,
  isCampaignComplete = false,
  resultsCell = null,
  onStop,
  isStopping = false,
  stopError = '',
  onMarkComplete,
  onReopen,
  isChangingCompletion = false,
  completionError = '',
  blockedOutbox = null
}) {
  // State for dropdown positioning (fixed position to escape overflow containers)
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const stackRef = useRef(null);

  const openActionDropdown = useCallback(() => {
    if (stackRef.current && allActionNotifications.length > 1) {
      const rect = stackRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom, // No gap - dropdown has padding-top for visual spacing
        left: rect.left
      });
      setDropdownOpen(true);
    }
  }, [allActionNotifications.length]);

  const closeActionDropdown = useCallback(() => {
    setDropdownOpen(false);
  }, []);

  const handleStackBlur = useCallback((event) => {
    const nextFocus = event.relatedTarget;
    if (stackRef.current?.contains(nextFocus)) return;
    if (nextFocus?.closest?.('.action-button-dropdown')) return;
    closeActionDropdown();
  }, [closeActionDropdown]);
  // Get button text based on notification type
  const getActionButtonText = (notification) => {
    if (!notification?.meta?.reason) return 'Action Needed';
    const reason = notification.meta.reason;
    const tourActionLabel = getTourActionLabel(reason);
    if (tourActionLabel) return tourActionLabel;
    if (reason === 'new_property_pending_approval') return 'New Property';
    if (reason === 'new_property_pending_send') return 'Send Property Email';
    if (reason.startsWith('wrong_contact:')) return 'Contact Redirect';
    if (reason.startsWith('needs_user_input:')) return getNeedsUserInputLabel(reason) ||
      reason.slice('needs_user_input:'.length).replace(/_/g, ' ');
    if (reason.startsWith('property_issue:critical')) return 'Critical Issue';
    if (reason.startsWith('property_issue:')) return 'Property Issue';
    if (reason.startsWith('contact_optout:')) return 'Opted Out';

    return 'Action Needed';
  };

  const renderActionEntry = notification => (
    <span className="action-entry">
      <span>{[getActionCardPropertyLabel(notification), getActionButtonText(notification)].filter(Boolean).join(' · ')}</span>
      {isCampaignLevelActionNotification(notification) && getActionCardDetails(notification) && (
        <span className="action-entry-details">{getActionCardDetails(notification)}</span>
      )}
    </span>
  );

  // Get button style based on notification type
  const getActionButtonClass = (notification) => {
    if (!notification?.meta?.reason) return 'btn--new-property';
    const reason = notification.meta.reason;

    if (reason.startsWith('property_issue:critical')) return 'btn--new-property btn--critical';
    if (reason.startsWith('contact_optout:')) return 'btn--new-property btn--optout';
    if (reason.startsWith('wrong_contact:')) return 'btn--new-property btn--redirect';

    return 'btn--new-property';
  };

  // Handle row click to toggle conversations
  const handleRowClick = (e) => {
    // Don't trigger row click if clicking on a button
    if (e.target.closest('button')) {
      return;
    }
    if (onToggleConversations) {
      onToggleConversations(client);
    }
  };

  const handleButtonClick = (e) => {
    // Stop propagation so button clicks don't trigger row click
    e.stopPropagation();
  };

  const campaignStatus = String(client.status || '').toLowerCase();
  const isNewCampaign = campaignStatus === 'new';
  // THE PILL NOW REFLECTS HER DECISION, not the product's guess.
  //
  // It used to read `status === 'completed' || isCampaignComplete` -- the first
  // being the string the retired inference wrote, the second a count of
  // resolved notifications. Neither was ever her saying so, and the backend
  // honours neither. A campaign the product had silently switched off still
  // displayed "Live"; one nobody had switched off could display "Completed".
  const markedComplete = campaignIsMarkedComplete(client);
  const isCompletedCampaign = markedComplete;
  // Every conversation has finished and nothing is outstanding. NOT a claim
  // that the campaign is over -- an observation, offered so the control can
  // tell her the moment is a natural one to take.
  const looksFinished = isCampaignComplete || Boolean(client.allThreadsTerminalAt);
  const stopStateLabel = {
    stopping: 'Stopping',
    stopped: 'Stopped',
    stop_failed: 'Stop failed'
  }[campaignStatus];
  const canRetryStop = campaignStatus === 'stop_failed';
  const stopLeaseValue = client.stopLeaseExpiresAt?.toDate?.() || client.stopLeaseExpiresAt;
  const stopLeaseExpiresAt = stopLeaseValue ? new Date(stopLeaseValue) : null;
  const hasLiveStopLease = stopLeaseExpiresAt && Number.isFinite(stopLeaseExpiresAt.getTime())
    ? stopLeaseExpiresAt.getTime() > Date.now()
    : false;
  const canResumeStop = campaignStatus === 'stopping' && !hasLiveStopLease;
  const canStopCampaign = campaignStatus !== 'completed'
    && !markedComplete
    && (['active', 'live', 'stop_failed'].includes(campaignStatus) || canResumeStop);
  const isStopRetry = canResumeStop || canRetryStop;

  // A campaign can be completed while it is RUNNING, and only then. A campaign
  // that has not started has nothing to finish; one that is stopping or stopped
  // is already not being read, and recording a completion over it would put a
  // decision in the log that she did not take.
  const canMarkComplete = Boolean(onMarkComplete)
    && !markedComplete
    && ['active', 'live'].includes(campaignStatus);
  const canReopen = Boolean(onReopen) && markedComplete;
  const markCompleteTitle = looksFinished
    ? 'Every conversation here has finished. Mark this campaign complete and SiteSift will stop watching it for broker replies. Nothing is deleted and you can reopen it at any time.'
    : 'Mark this campaign complete. SiteSift will stop watching it for broker replies. Nothing is deleted and you can reopen it at any time.';
  const sheetActionAriaLabel = `Open source sheet for ${client.name || 'client'}`;
  // Emails the send path refused used to leave no mark at all: the queued badge
  // ticked down to zero and the campaign looked sent. This is the row saying
  // out loud what did not go.
  const blockedOutboxNotice = describeBlockedOutbox(blockedOutbox || {});

  return (
    <tr
      className={`client-row ${isConversationsExpanded ? 'client-row--expanded' : ''}`}
      onClick={handleRowClick}
    >
      {/* Status column - first column with expand icon + status button */}
      <td className="td--status">
        <div className="status-cell-wrapper">
        <div className="status-cell">
          {/* Expand/Collapse indicator */}
          <div className="expand-indicator">
            {isConversationsExpanded ? (
              <ChevronDown size={18} className="expand-icon" />
            ) : (
              <ChevronRight size={18} className="expand-icon" />
            )}
            {notificationCount > 0 && (
              <span className="notification-count-badge">
                {notificationCount > 99 ? '99+' : notificationCount}
              </span>
            )}
          </div>

          {/* Status button/indicator */}
          {isNewCampaign ? (
            <button
              className="btn btn--status"
              onClick={(e) => {
                handleButtonClick(e);
                if (onStart) onStart(client);
              }}
            >
              Get Started
            </button>
          ) : allActionNotifications.length > 0 ? (
            <div
              className="action-button-stack"
              ref={stackRef}
              onMouseEnter={openActionDropdown}
              onMouseLeave={closeActionDropdown}
              onFocus={openActionDropdown}
              onBlur={handleStackBlur}
            >
              {stopStateLabel && <span className="status-text">{stopStateLabel}</span>}
              {/* Top button - stays in normal flow */}
              <button
                className={`btn btn--status btn--stacked btn--stacked-top ${getActionButtonClass(allActionNotifications[0])}`}
                aria-haspopup={allActionNotifications.length > 1 ? 'menu' : undefined}
                aria-expanded={allActionNotifications.length > 1 ? dropdownOpen : undefined}
                onClick={(e) => {
                  handleButtonClick(e);
                  if (onActionClick) onActionClick(client, allActionNotifications[0]);
                }}
              >
                {renderActionEntry(allActionNotifications[0])}
              </button>

              {/* Dropdown buttons - rendered via portal to escape overflow */}
              {dropdownOpen && allActionNotifications.length > 1 && createPortal(
                <div
                  className="action-button-dropdown"
                  style={{
                    position: 'fixed',
                    top: dropdownPosition.top,
                    left: dropdownPosition.left,
                    zIndex: 9999
                  }}
                  onMouseEnter={() => setDropdownOpen(true)}
                  onMouseLeave={() => setDropdownOpen(false)}
                >
                  {allActionNotifications.slice(1).map((notification, index) => (
                    <button
                      key={notification.id}
                      className={`btn btn--status btn--stacked ${getActionButtonClass(notification)}`}
                      style={{
                        animationDelay: `${index * 0.02}s`,
                        opacity: 0,
                        animation: 'dropdownFadeIn 0.15s ease forwards'
                      }}
                      onClick={(e) => {
                        handleButtonClick(e);
                        setDropdownOpen(false);
                        if (onActionClick) onActionClick(client, notification);
                      }}
                    >
                      {renderActionEntry(notification)}
                    </button>
                  ))}
                </div>,
                document.body
              )}
            </div>
          ) : hasActionPending ? (
            <button
              className={`btn btn--status ${getActionButtonClass(actionableNotification)}`}
              onClick={(e) => {
                handleButtonClick(e);
                if (onActionClick) onActionClick(client, actionableNotification);
              }}
            >
              {stopStateLabel && <span className="status-text">{stopStateLabel}</span>}
              {renderActionEntry(actionableNotification)}
            </button>
          ) : stopStateLabel ? (
            <div className={`status-indicator status-indicator--${campaignStatus}`}>
              <div className={`status-dot status-dot--${campaignStatus}`}></div>
              <span className="status-text">{stopStateLabel}</span>
            </div>
          ) : (
            <div className="status-indicator">
              <div className={`status-dot ${isCompletedCampaign ? 'status-dot--completed' : 'status-dot--live'}`}></div>
              <span className="status-text">{isCompletedCampaign ? 'Completed' : 'Live'}</span>
            </div>
          )}
          {canStopCampaign && (
            <button
              type="button"
              className="campaign-stop-button"
              aria-label={`${isStopping ? 'Stopping' : isStopRetry ? 'Retry stop campaign' : 'Stop campaign'} ${client.name || 'client'}`}
              title={isStopping ? 'Stopping campaign' : isStopRetry ? 'Retry stop campaign' : 'Stop campaign'}
              disabled={isStopping}
              onClick={(e) => {
                handleButtonClick(e);
                if (!isStopping && onStop) onStop(client);
              }}
            >
              {isStopping
                ? <LoaderCircle className="campaign-stop-button__spinner" size={16} aria-hidden="true" />
                : <CircleStop size={16} aria-hidden="true" />}
            </button>
          )}
          {/* MARKING A CAMPAIGN COMPLETE lives here, beside Stop, because this
              cell is already where a campaign's state is shown and already
              where she acts on it. Completion is what bounds the work: until
              she takes this decision the product must keep watching every
              conversation on the campaign, forever. */}
          {canMarkComplete && (
            <button
              type="button"
              className={`campaign-complete-button${looksFinished ? ' campaign-complete-button--suggested' : ''}`}
              aria-label={`Mark campaign complete ${client.name || 'client'}`}
              title={markCompleteTitle}
              disabled={isChangingCompletion}
              onClick={(e) => {
                handleButtonClick(e);
                if (!isChangingCompletion && onMarkComplete) onMarkComplete(client);
              }}
            >
              {isChangingCompletion
                ? <LoaderCircle className="campaign-stop-button__spinner" size={16} aria-hidden="true" />
                : <CircleCheck size={16} aria-hidden="true" />}
            </button>
          )}
          {canReopen && (
            <button
              type="button"
              className="campaign-reopen-button"
              aria-label={`Reopen campaign ${client.name || 'client'}`}
              title="Reopen this campaign. SiteSift will start watching it for broker replies again."
              disabled={isChangingCompletion}
              onClick={(e) => {
                handleButtonClick(e);
                if (!isChangingCompletion && onReopen) onReopen(client);
              }}
            >
              {isChangingCompletion
                ? <LoaderCircle className="campaign-stop-button__spinner" size={16} aria-hidden="true" />
                : <RotateCcw size={16} aria-hidden="true" />}
            </button>
          )}
        </div>
        {stopError && <div className="campaign-stop-error" role="alert">{stopError}</div>}
        {completionError && <div className="campaign-stop-error" role="alert">{completionError}</div>}
        {blockedOutboxNotice && (
          <div className="campaign-blocked-outbox" role="alert">
            <span>{blockedOutboxNotice.message}</span>
            {blockedOutboxNotice.needsMailboxConnection && <ConnectMailboxButton />}
          </div>
        )}
        </div>
      </td>

      {/* Name */}
      <td>{client.name}</td>

      {/* Last Updated */}
      <td>{client.lastUpdated ?? '—'}</td>

      {resultsCell}

      {/* Open Source Sheet */}
      <td className="clients-table__sheet-cell">
        <button
          type="button"
          className="btn btn--open btn--sheet"
          aria-label={sheetActionAriaLabel}
          onClick={(e) => {
            handleButtonClick(e);
            if (onOpenSheet) onOpenSheet(client.id);
            else if (onOpen) onOpen(client.id);
          }}
          title="Open source sheet"
        >
          <Table2 size={15} />
          Sheet
        </button>
      </td>
    </tr>
  );
}
